/**
 * First-Time User Promotion Templates
 * Welcome new users and drive initial conversions
 */

import { PromotionTemplate } from "./types";

export const FIRST_TIME_TEMPLATES: PromotionTemplate[] = [
  {
    id: "welcome-discount",
    name: "Welcome Discount",
    category: "first_time",
    description: "Greet new users with an exclusive first-time discount",
    strategy: "First impressions matter. A welcome discount creates goodwill and reduces friction for first-time purchases. Best shown after initial onboarding is complete.",
    icon: "SmileOutlined",
    color: "#10B981",
    expectedOutcomes: [
      "Higher first-week conversion",
      "Positive first impression",
      "Faster time to first purchase",
    ],
    bestPractices: [
      "Show after user completes onboarding",
      "Make them feel special/exclusive",
      "Set clear expiration (7 days)",
      "Don't overwhelm - one clear offer",
    ],
    tags: ["first-time", "welcome", "new-user", "onboarding"],
    bestFor: ["All SaaS products", "Consumer apps", "E-commerce"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 8,
      targetAudience: ["new"],
      discount: {
        discountType: "percentage",
        discountValue: 30,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "default",
        primaryColor: "#10B981",
        secondaryColor: "#3B82F6",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 3,
        delaySeconds: 10,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Welcome to Copilotly!",
          subheadline: "Here's 30% off your first subscription. Just for you.",
          ctaText: "Claim My Discount",
          ctaSecondaryText: "Explore First",
          badge: "NEW USER",
          terms: "*Offer valid for 7 days from signup.",
        },
      },
    },
  },
  {
    id: "first-purchase-bonus",
    name: "First Purchase Bonus",
    category: "first_time",
    description: "Add extra value to first-time purchases",
    strategy: "Instead of discounting, add extra value. This maintains perceived product value while still incentivizing the first purchase. Great for credit-based products.",
    icon: "GiftOutlined",
    color: "#8B5CF6",
    expectedOutcomes: [
      "Higher perceived value",
      "First purchase conversion",
      "No discount expectation set",
    ],
    bestPractices: [
      "Add credits or bonus features",
      "Don't discount the core price",
      "Make bonus feel substantial",
      "Time-limit the bonus",
    ],
    tags: ["first-time", "bonus", "value-add", "credits"],
    bestFor: ["Credit-based products", "Usage-based pricing"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "festive",
      priority: 7,
      targetAudience: ["new", "free"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 0,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#8B5CF6",
        secondaryColor: "#EC4899",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 3,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Double Your Credits!",
          subheadline: "Subscribe now and get 2x credits for your first month.",
          ctaText: "Get Double Credits",
          ctaSecondaryText: "Learn More",
          badge: "2X BONUS",
          terms: "*Bonus credits added to first month only.",
        },
      },
    },
  },
  {
    id: "extended-trial",
    name: "Extended Trial Offer",
    category: "first_time",
    description: "Offer an extended trial to hesitant new users",
    strategy: "Some users need more time to evaluate. An extended trial offer can convert users who would otherwise leave. Best used for complex products.",
    icon: "ClockCircleOutlined",
    color: "#3B82F6",
    expectedOutcomes: [
      "Higher trial completion",
      "More time to demonstrate value",
      "Conversion of hesitant users",
    ],
    bestPractices: [
      "Offer 7-14 extra days",
      "Require engagement to unlock",
      "Follow up during extension",
      "Don't extend indefinitely",
    ],
    tags: ["first-time", "trial", "extended", "evaluation"],
    bestFor: ["Complex products", "B2B SaaS", "Learning curves"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 7,
      targetAudience: ["trial"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 0,
        currency: "USD",
        applicablePlans: ["pro"],
      },
      styling: {
        theme: "default",
        primaryColor: "#3B82F6",
        secondaryColor: "#6366F1",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 2,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Need More Time?",
          subheadline: "We get it. Here's 7 extra days to explore everything.",
          ctaText: "Extend My Trial",
          ctaSecondaryText: "Subscribe Now",
          badge: "+7 DAYS",
          terms: "*One extension per account.",
        },
      },
    },
  },
  {
    id: "exit-intent-newuser",
    name: "Exit Intent (New User)",
    category: "first_time",
    description: "Capture leaving new users with a last-chance offer",
    strategy: "Exit intent popups catch users about to leave. For new users, this is your last chance to convert. Make the offer compelling enough to stop them.",
    icon: "ExportOutlined",
    color: "#EF4444",
    expectedOutcomes: [
      "Reduced bounce rate",
      "Last-chance conversions",
      "Email capture fallback",
    ],
    bestPractices: [
      "Make offer impossible to ignore",
      "Include email capture option",
      "Don't be aggressive - one popup",
      "Offer value, not desperation",
    ],
    tags: ["first-time", "exit-intent", "last-chance", "conversion"],
    bestFor: ["High-traffic sites", "Products with quick evaluation"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "exit_intent",
      displayStyle: "bold",
      priority: 10,
      targetAudience: ["new", "free"],
      discount: {
        discountType: "percentage",
        discountValue: 40,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "dark",
        primaryColor: "#EF4444",
        secondaryColor: "#F97316",
        animation: "shake",
      },
      displayConditions: {
        showOnPages: ["pricing"],
        maxImpressionsPerUser: 1,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Wait! Don't Leave Empty-Handed",
          subheadline: "Here's 40% off - our best offer. Just for you, just for now.",
          ctaText: "Claim 40% Off",
          ctaSecondaryText: "No Thanks",
          badge: "LAST CHANCE",
          terms: "*Offer expires when you leave this page.",
        },
      },
    },
  },
  {
    id: "signup-anniversary-24h",
    name: "24-Hour Signup Special",
    category: "first_time",
    description: "Time-limited offer within 24 hours of signup",
    strategy: "Create urgency from the moment of signup. Users are most engaged in first 24 hours. A time-limited discount capitalizes on this peak interest.",
    icon: "FireOutlined",
    color: "#F97316",
    expectedOutcomes: [
      "Immediate conversions",
      "Capture high-intent users",
      "Reduced time to revenue",
    ],
    bestPractices: [
      "Show countdown from signup time",
      "Make discount substantial",
      "Don't repeat after expiry",
      "Combine with onboarding",
    ],
    tags: ["first-time", "urgency", "24-hour", "signup"],
    bestFor: ["Products with clear value prop", "Simple purchasing"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "banner",
      displayStyle: "bold",
      priority: 9,
      targetAudience: ["new"],
      discount: {
        discountType: "percentage",
        discountValue: 50,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_blue",
        primaryColor: "#F97316",
        secondaryColor: "#EF4444",
        animation: "pulse",
      },
      displayConditions: {
        showOnPages: ["all"],
        maxImpressionsPerUser: 10,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "New User Special!",
          subheadline: "50% off - but only for the next 24 hours!",
          ctaText: "Get 50% Off Now",
          ctaSecondaryText: "Explore First",
          badge: "24HR ONLY",
          terms: "*Offer expires 24 hours after signup.",
        },
      },
    },
  },
];
